/**
 * Veluna Meta storefront tracking for the live theme export.
 * Sends explicit single-pixel Meta events and forwards matching CAPI payloads to AdForge.
 */

(function() {
  'use strict';

  var META_CART_CONTENT_IDS_KEY = 'veluna_meta_cart_content_ids';
  var RITUAL_PRODUCT_MAP = {
    mc: 'mind-calm-ritual-set',
    dr: 'deep-restore-ritual-set',
    ss: 'sacred-soothe-ritual-set',
    cr: 'circadian-reset-ritual-set'
  };
  var TRACKED_META_EVENTS = {
    add_to_cart: 'AddToCart',
    begin_checkout: 'InitiateCheckout',
    view_item: 'ViewContent',
    quiz_complete: 'ViewContent',
    email_captured: 'Lead',
    quiz_lead_capture: 'Lead'
  };

  function safeParseJSON(rawValue) {
    if (!rawValue) {
      return {};
    }

    try {
      var parsed = JSON.parse(rawValue);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function normalizeString(value) {
    return String(value || '').trim();
  }

  function normalizeEmail(value) {
    return normalizeString(value).toLowerCase();
  }

  function normalizePhone(value) {
    return normalizeString(value).replace(/[^0-9+]/g, '');
  }

  function normalizeRitualCode(value) {
    var normalized = normalizeString(value).toLowerCase().replace(/[^a-z]/g, '');
    if (!normalized) {
      return '';
    }

    if (normalized === 'mindcalm' || normalized === 'mc') return 'mc';
    if (normalized === 'deeprestore' || normalized === 'dr') return 'dr';
    if (normalized === 'sacredsoothe' || normalized === 'ss') return 'ss';
    if (normalized === 'circadianreset' || normalized === 'cr') return 'cr';
    return normalized;
  }

  function uniqueValues(values) {
    return Array.from(new Set((Array.isArray(values) ? values : [values]).map(function(value) {
      return normalizeString(value);
    }).filter(Boolean)));
  }

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function getThemeSettings() {
    window.VelunaThemeSettings = window.VelunaThemeSettings || {};

    if (!window.VelunaThemeSettings.metaPixelId) {
      window.VelunaThemeSettings.metaPixelId = '1955985578388607';
    }

    /* 2026-09-23: CAPI relay off (Cloud Run host down, GCP billing closed). An empty
       endpoint skips it. Old value: https://studio.velunasleep.com/api/deploy/meta/storefront-conversions */
    if (typeof window.VelunaThemeSettings.metaConversionsEndpoint === 'undefined') {
      window.VelunaThemeSettings.metaConversionsEndpoint = '';
    }

    if (!window.VelunaThemeSettings.storefrontOrigin) {
      window.VelunaThemeSettings.storefrontOrigin = window.location.origin;
    }

    return window.VelunaThemeSettings;
  }

  function getQuizState() {
    var ritualState = window.VelunaRitual || {};
    var quizResults = window.QuizResults || {};
    var storedResult = safeParseJSON(localStorage.getItem('veluna_quiz_result'));
    var storedLegacyResult = safeParseJSON(localStorage.getItem('veluna_quiz_results'));
    var ritualCode = normalizeRitualCode(
      ritualState.ritual ||
      ritualState.ritualCode ||
      quizResults.ritual ||
      quizResults.ritualCode ||
      storedResult.ritual ||
      storedResult.ritualCode ||
      storedLegacyResult.category
    );
    var recommendedProducts = uniqueValues(
      ritualState.recommendedProducts ||
      quizResults.recommendedProducts ||
      storedResult.recommendedProducts ||
      (RITUAL_PRODUCT_MAP[ritualCode] ? [RITUAL_PRODUCT_MAP[ritualCode]] : [])
    );

    return {
      ritualCode: ritualCode,
      ritualName: normalizeString(
        ritualState.ritualName ||
        quizResults.ritualName ||
        storedResult.ritualName ||
        storedLegacyResult.ritual
      ),
      recommendedProducts: recommendedProducts
    };
  }

  function getMetaContext(overrides) {
    var source = overrides || {};
    var quizState = getQuizState();
    var behaviorStage = normalizeString(source.behavior_stage || source.behaviorStage);

    if (!behaviorStage) {
      if (window.location.pathname.indexOf('/cart') === 0) {
        behaviorStage = 'cart_view';
      } else if (window.location.pathname.indexOf('/products/') === 0) {
        behaviorStage = 'organic_pdp_view';
      } else {
        behaviorStage = 'site_view';
      }
    }

    return {
      ritualCode: quizState.ritualCode,
      ritualName: quizState.ritualName,
      recommendedProducts: quizState.recommendedProducts,
      metaSegment: quizState.ritualCode ? 'ritual_' + quizState.ritualCode : '',
      recommendationSource: quizState.ritualCode ? 'quiz' : 'site',
      behaviorStage: behaviorStage
    };
  }

  function readStoredCartContentIds() {
    try {
      return uniqueValues(JSON.parse(localStorage.getItem(META_CART_CONTENT_IDS_KEY) || '[]'));
    } catch (error) {
      return [];
    }
  }

  function writeStoredCartContentIds(contentIds) {
    var normalizedIds = uniqueValues(contentIds);
    if (!normalizedIds.length) {
      localStorage.removeItem(META_CART_CONTENT_IDS_KEY);
      return;
    }

    localStorage.setItem(META_CART_CONTENT_IDS_KEY, JSON.stringify(normalizedIds));
  }

  function getPageProductHandle() {
    var explicit = document.body && normalizeString(document.body.getAttribute('data-product-handle'));
    if (explicit) {
      return explicit;
    }

    var matchedElement = document.querySelector('[data-product-handle], [data-jarvis-product-handle]');
    if (matchedElement) {
      return normalizeString(
        matchedElement.getAttribute('data-product-handle') ||
        matchedElement.getAttribute('data-jarvis-product-handle')
      );
    }

    var pathMatch = window.location.pathname.match(/^\/products\/([^/?#]+)/);
    return pathMatch ? decodeURIComponent(pathMatch[1]) : '';
  }

  function getElementProductHandle(element) {
    if (!element) {
      return '';
    }

    var directHandle = normalizeString(
      element.getAttribute && (element.getAttribute('data-product-handle') || element.getAttribute('data-jarvis-product-handle'))
    );
    if (directHandle) {
      return directHandle;
    }

    var closestHandle = element.closest && element.closest('[data-product-handle], [data-jarvis-product-handle]');
    if (closestHandle) {
      return normalizeString(
        closestHandle.getAttribute('data-product-handle') ||
        closestHandle.getAttribute('data-jarvis-product-handle')
      );
    }

    return '';
  }

  function getCartContentIds() {
    if (window.cart && Array.isArray(window.cart.items)) {
      var cartHandles = uniqueValues(window.cart.items.map(function(item) {
        return item.handle || (item.product && item.product.handle) || '';
      }));
      if (cartHandles.length) {
        return cartHandles;
      }
    }

    var selectors = [
      '.cart-item [data-product-handle]',
      '.cart-item [data-jarvis-product-handle]',
      '[data-cart-item] [data-product-handle]',
      '[data-cart-item] [data-jarvis-product-handle]',
      '[data-line-item] [data-product-handle]',
      '[data-line-item] [data-jarvis-product-handle]'
    ];

    var values = [];
    selectors.forEach(function(selector) {
      document.querySelectorAll(selector).forEach(function(node) {
        values.push(node.getAttribute('data-product-handle') || node.getAttribute('data-jarvis-product-handle') || '');
      });
    });

    return uniqueValues(values);
  }

  function resolveContentIds(eventData, options) {
    var settings = options || {};
    var explicitIds = uniqueValues(eventData && eventData.content_ids);
    if (explicitIds.length) {
      return explicitIds;
    }

    var productHandle = normalizeString((eventData && eventData.product_handle) || '');
    if (!productHandle && settings.sourceElement) {
      productHandle = getElementProductHandle(settings.sourceElement);
    }
    if (!productHandle) {
      productHandle = getPageProductHandle();
    }
    if (productHandle) {
      return [productHandle];
    }

    if (settings.preferCart) {
      var cartHandles = getCartContentIds();
      if (cartHandles.length) {
        return cartHandles;
      }
    }

    var quizState = getQuizState();
    if (quizState.recommendedProducts.length) {
      return [quizState.recommendedProducts[0]];
    }

    if (settings.preferStoredCart !== false) {
      var storedCartIds = readStoredCartContentIds();
      if (storedCartIds.length) {
        return storedCartIds;
      }
    }

    var fallbackIds = uniqueValues(eventData && eventData.product_id);
    return fallbackIds.filter(function(id) { return !/^\d+$/.test(id); });
  }

  function syncStoredCartContentIds(eventName, eventData, contentIds) {
    var normalizedEvent = normalizeString(eventName).toLowerCase();
    var behaviorStage = normalizeString((eventData && eventData.behavior_stage) || '').toLowerCase();

    if (normalizedEvent === 'purchase' || behaviorStage === 'purchase') {
      writeStoredCartContentIds([]);
      return;
    }

    if (normalizedEvent === 'addtocart' || normalizedEvent === 'initiatecheckout' || behaviorStage.indexOf('add_to_cart') !== -1 || behaviorStage.indexOf('checkout') !== -1) {
      writeStoredCartContentIds(readStoredCartContentIds().concat(contentIds));
    }
  }

  function persistIdentifiers(eventData) {
    if (!eventData) {
      return;
    }

    var email = normalizeEmail(eventData.email);
    var phone = normalizePhone(eventData.phone);
    if (email && email.indexOf('@') !== -1) {
      localStorage.setItem('veluna_customer_email', email);
      localStorage.setItem('veluna_quiz_email', email);
    }
    if (phone) {
      localStorage.setItem('veluna_customer_phone', phone);
    }
  }

  function sha256Hex(value) {
    if (!value || !window.crypto || !window.crypto.subtle || typeof TextEncoder === 'undefined') {
      return Promise.resolve('');
    }

    return window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)).then(function(buffer) {
      return Array.from(new Uint8Array(buffer)).map(function(byte) {
        return byte.toString(16).padStart(2, '0');
      }).join('');
    }).catch(function() {
      return '';
    });
  }

  function buildUserData() {
    var email = normalizeEmail(localStorage.getItem('veluna_customer_email') || localStorage.getItem('veluna_quiz_email'));
    var phone = normalizePhone(localStorage.getItem('veluna_customer_phone'));
    var externalId = localStorage.getItem('veluna_customer_id') || '';
    var userData = {
      client_user_agent: navigator.userAgent || '',
      fbp: readCookie('_fbp'),
      fbc: readCookie('_fbc')
    };

    if (externalId) {
      userData.external_id = [externalId];
    }

    return Promise.all([
      sha256Hex(email),
      sha256Hex(phone)
    ]).then(function(results) {
      if (results[0]) {
        userData.em = [results[0]];
      }
      if (results[1]) {
        userData.ph = [results[1]];
      }
      return userData;
    });
  }

  function buildEventId(eventName, eventData) {
    if (eventData && eventData.event_id) {
      return normalizeString(eventData.event_id);
    }

    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'veluna_' + window.crypto.randomUUID();
    }

    return ['veluna', normalizeString(eventName).toLowerCase(), Date.now().toString(36), Math.random().toString(36).slice(2, 10)].join('_');
  }

  function ensureMetaPixelInitialized() {
    var settings = getThemeSettings();
    var pixelId = normalizeString(settings.metaPixelId);
    if (!pixelId) {
      return;
    }

    if (!window.fbq) {
      !function(f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function() {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }

    if (window.VelunaConsent && !window.VelunaConsent.marketingAllowed()) {
      try { window.VelunaConsent.note('fbq_init'); } catch (e) {}
      return;
    }
    if (!window.__VELUNA_META_PIXEL_INITIALIZED__) {
      window.fbq('init', pixelId);
      window.__VELUNA_META_PIXEL_INITIALIZED__ = true;
    }
  }

  function sendMetaConversionEvent(eventName, eventData) {
    /* consent 2026-09-11: the relay carries hashed email/phone + fbp/fbc to an advertising
       endpoint, so it is marketing data and must follow the same gate as fbq init. */
    if (window.VelunaConsent && !window.VelunaConsent.marketingAllowed()) {
      try { window.VelunaConsent.note('capi_relay'); } catch (e) {}
      return;
    }
    var settings = getThemeSettings();
    var endpoint = normalizeString(settings.metaConversionsEndpoint);
    if (!endpoint || typeof fetch === 'undefined') {
      return;
    }

    var context = getMetaContext(eventData);
    var contentIds = resolveContentIds(eventData, {
      preferCart: eventName === 'InitiateCheckout' || eventName === 'Purchase'
    });

    syncStoredCartContentIds(eventName, eventData, contentIds);
    persistIdentifiers(eventData);

    buildUserData().then(function(userData) {
      var payload = {
        event_name: eventName,
        event_id: normalizeString(eventData.event_id),
        event_source_url: window.location.href,
        user_data: userData,
        custom_data: {
          value: Number(eventData.value || 0),
          currency: normalizeString(eventData.currency || 'USD') || 'USD',
          content_ids: contentIds,
          content_name: normalizeString(eventData.product_name || eventData.content_name || ''),
          content_category: normalizeString(eventData.content_category || context.ritualName || 'Sleep Supplements') || 'Sleep Supplements',
          behavior_stage: context.behaviorStage,
          meta_segment: context.metaSegment,
          recommendation_source: context.recommendationSource,
          product_handle: normalizeString(eventData.product_handle || contentIds[0] || '')
        }
      };

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        mode: 'cors'
      }).catch(function(error) {
        console.warn('Veluna Meta Tracking: CAPI forward failed', error);
      });
    });
  }

  /* 2026-09-23: the Facebook & Instagram app pixel owns these events (incl. Purchase
     from checkout). Sending them here too would double count. */
  var VL_APP_OWNS = ['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase'];

  function trackMetaEvent(eventName, eventData) {
    if (VL_APP_OWNS.indexOf(eventName) !== -1) { return; }
    var settings = getThemeSettings();
    var pixelId = normalizeString(settings.metaPixelId);
    var context = getMetaContext(eventData);
    var eventId = buildEventId(eventName, eventData || {});
    var payloadData = Object.assign({}, eventData || {}, { event_id: eventId });
    var contentIds = resolveContentIds(payloadData, {
      preferCart: eventName === 'InitiateCheckout' || eventName === 'Purchase'
    });
    var payload = {
      content_name: normalizeString(payloadData.product_name || payloadData.content_name || ''),
      content_ids: contentIds,
      content_type: normalizeString(payloadData.content_type || 'product') || 'product',
      content_category: normalizeString(payloadData.content_category || context.ritualName || 'Sleep Supplements') || 'Sleep Supplements',
      value: Number(payloadData.value || 0),
      currency: normalizeString(payloadData.currency || 'USD') || 'USD',
      behavior_stage: context.behaviorStage,
      meta_segment: context.metaSegment,
      recommendation_source: context.recommendationSource,
      product_handle: normalizeString(payloadData.product_handle || contentIds[0] || '')
    };

    syncStoredCartContentIds(eventName, payloadData, contentIds);
    persistIdentifiers(payloadData);
    ensureMetaPixelInitialized();

    if (typeof window.fbq !== 'undefined' && pixelId) {
      window.fbq('trackSingle', pixelId, eventName, payload, { eventID: eventId });
    }

    sendMetaConversionEvent(eventName, Object.assign({}, payloadData, payload));
  }

  function trackEvent(eventName, eventData) {
    var normalizedName = normalizeString(eventName).toLowerCase();
    var payload = Object.assign({}, eventData || {});

    if (!payload.behavior_stage) {
      payload.behavior_stage = normalizedName;
    }

    var mappedEvent = TRACKED_META_EVENTS[normalizedName];
    if (mappedEvent) {
      trackMetaEvent(mappedEvent, payload);
    }

    if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: normalizedName }, payload));
    }
  }

  function trackProductView() {
    if (window.location.pathname.indexOf('/products/') !== 0) {
      return;
    }

    var productHandle = getPageProductHandle();
    if (!productHandle) {
      return;
    }

    var titleNode = document.querySelector('h1, [data-product-title]');
    var priceNode = document.querySelector('[data-product-price], .price-item--regular, .product-price');
    var priceValue = priceNode ? Number(String(priceNode.textContent || '').replace(/[^0-9.]/g, '')) : 0;

    function fireViewContent(price) {
      trackMetaEvent('ViewContent', {
        product_handle: productHandle,
        product_name: titleNode ? normalizeString(titleNode.textContent) : '',
        value: price,
        currency: 'USD',
        behavior_stage: 'organic_pdp_view'
      });
    }

    if (priceValue > 0) {
      fireViewContent(priceValue);
    } else {
      fetch('/products/' + encodeURIComponent(productHandle) + '.js').then(function(r) {
        return r.json();
      }).then(function(product) {
        var p = product && product.variants && product.variants[0] ? Number(product.variants[0].price) / 100 : 0;
        fireViewContent(p);
      }).catch(function() {
        fireViewContent(0);
      });
    }
  }

  /* 2026-09-23: adds on this theme are fetch() calls, so hook them. Fires only on a
     successful response, with the line item Shopify returns (true handle, qty, price). */
  function initAddToCartFetchTracking() {
    if (!window.fetch || window.__vlAtcFetchHooked) return;
    window.__vlAtcFetchHooked = true;
    var origFetch = window.fetch;
    window.fetch = function(input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var promise = origFetch.apply(this, arguments);
      if (method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url)) {
        promise.then(function(res) {
          if (!res || !res.ok) return;
          res.clone().json().then(function(data) {
            var items = data && data.items ? data.items : [data];
            items.forEach(function(it) {
              if (!it || !it.handle) return;
              window.__vlAtcTs = Date.now();
              var cents = it.final_line_price || it.line_price || ((it.price || 0) * (it.quantity || 1));
              trackMetaEvent('AddToCart', {
                product_handle: it.handle,
                product_name: normalizeString(it.product_title || it.title || ''),
                value: Number(cents || 0) / 100,
                currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
                behavior_stage: 'add_to_cart'
              });
            });
          }).catch(function() {});
        }).catch(function() {});
      }
      return promise;
    };
  }

  function initAddToCartTracking() {
    initAddToCartFetchTracking();
    document.addEventListener('submit', function(event) {
      var form = event.target;
      if (!form || !form.matches('form[action*="/cart/add"]')) {
        return;
      }
      /* fallback only: skip if the fetch hook already reported this add */
      var submittedAt = Date.now();
      setTimeout(function() {
      if (window.__vlAtcTs && window.__vlAtcTs >= submittedAt - 50) {
        return;
      }

      var formData = new FormData(form);
      var quantity = Number(formData.get('quantity') || 1);
      var handle = getElementProductHandle(form) || getPageProductHandle();
      var titleNode = document.querySelector('h1, [data-product-title]');
      var priceNode = document.querySelector('[data-product-price], .price-item--regular, .product-price');
      var unitPrice = priceNode ? Number(String(priceNode.textContent || '').replace(/[^0-9.]/g, '')) : 0;

      function fireAddToCart(price) {
        trackMetaEvent('AddToCart', {
          product_handle: handle,
          product_name: titleNode ? normalizeString(titleNode.textContent) : '',
          value: price * quantity,
          currency: 'USD',
          behavior_stage: 'add_to_cart'
        });
      }

      if (unitPrice > 0) {
        fireAddToCart(unitPrice);
      } else if (handle) {
        fetch('/products/' + encodeURIComponent(handle) + '.js').then(function(r) {
          return r.json();
        }).then(function(product) {
          var p = product && product.variants && product.variants[0] ? Number(product.variants[0].price) / 100 : 0;
          fireAddToCart(p);
        }).catch(function() {
          fireAddToCart(0);
        });
      } else {
        fireAddToCart(0);
      }
      }, 1500);
    }, true);
  }

  function initCheckoutTracking() {
    document.addEventListener('click', function(event) {
      var trigger = event.target && event.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
      if (!trigger) {
        return;
      }

      var contentIds = getCartContentIds();
      trackMetaEvent('InitiateCheckout', {
        content_ids: contentIds,
        product_handle: contentIds[0] || '',
        value: window.cart && typeof window.cart.total_price === 'number' ? Number(window.cart.total_price) / 100 : 0,
        currency: window.cart && window.cart.currency ? window.cart.currency : 'USD',
        behavior_stage: 'begin_checkout'
      });
    }, true);
  }

  function initQuizTracking() {
    window.addEventListener('quiz:results-ready', function(event) {
      void event;
    });

    window.addEventListener('jarvis:event', function(event) {
      var detail = (event && event.detail) || {};
      if (!detail.event) {
        return;
      }

      if (detail.event === 'quiz_results_view') {
        return;
      }

      if (detail.event === 'quiz_lead_capture') {
        trackMetaEvent('Lead', {
          email: detail.email,
          behavior_stage: 'quiz_lead_capture'
        });
        return;
      }

      trackEvent(detail.event, detail);
    });
  }

  function captureShopifyCustomerData() {
    try {
      // Shopify exposes customer data via meta tags and __st object
      var customerMeta = document.querySelector('meta[name="shopify-customer-id"]');
      if (customerMeta && customerMeta.content) {
        localStorage.setItem('veluna_customer_id', customerMeta.content);
      }

      // Shopify's ShopifyAnalytics exposes customer info
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) {
        var custId = window.ShopifyAnalytics.meta.page.customerId;
        if (custId) {
          localStorage.setItem('veluna_customer_id', String(custId));
        }
      }

      // Also capture from Shopify's __st cookie-like data
      if (window.__st && window.__st.cid) {
        localStorage.setItem('veluna_customer_id', String(window.__st.cid));
      }

      // Capture customer email/phone from Shopify's customer object if logged in
      if (window.meta && window.meta.product) {
        // On product pages, Shopify sometimes exposes this
      }
    } catch (e) {
      // Fail silently — customer data capture is best-effort
    }
  }

  function captureNewsletterEmails() {
    // Intercept newsletter signup forms to persist email for advanced matching
    document.addEventListener('submit', function(event) {
      var form = event.target;
      if (!form || !form.matches) return;

      // Newsletter forms typically post to /contact or have specific identifiers
      var action = (form.getAttribute('action') || '').toLowerCase();
      var isNewsletter = action.indexOf('/contact') !== -1 ||
        form.querySelector('input[name="contact[tags]"][value*="newsletter"]') ||
        form.classList.contains('newsletter') ||
        form.closest('.newsletter, .footer-newsletter, [data-newsletter]');

      if (!isNewsletter) return;

      var emailInput = form.querySelector('input[type="email"], input[name="contact[email]"]');
      if (emailInput && emailInput.value) {
        var email = normalizeEmail(emailInput.value);
        if (email && email.indexOf('@') !== -1) {
          localStorage.setItem('veluna_customer_email', email);
          // Fire Lead event for newsletter signups
          trackMetaEvent('Lead', {
            email: email,
            behavior_stage: 'newsletter_signup'
          });
        }
      }
    }, true);
  }

  function init() {
    getThemeSettings();
    ensureMetaPixelInitialized();
    captureShopifyCustomerData();
    trackProductView();
    initAddToCartTracking();
    initCheckoutTracking();
    initQuizTracking();
    captureNewsletterEmails();

    window.VelunaAnalytics = Object.assign(window.VelunaAnalytics || {}, {
      getMetaContext: getMetaContext,
      sendMetaConversionEvent: sendMetaConversionEvent,
      trackEvent: trackEvent,
      track: trackEvent
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();